import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { usePermissions } from "../../PermissionsProvider";
import { AgentService } from "../../services/AgentService";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Card, CardContent } from "../ui/card";
import { Skeleton } from "../ui/skeleton";

interface Node {
  agent_id: number;
  agent_name: string;
  agent_description: string;
  request_count: number;
  total_execution_time_ms: number;
  action_count?: number;
  total_action_time_ms?: number;
}

interface Edge {
  from_agent_id: number;
  to_agent_id: number;
  from_request_id: string | null;
  to_request_id: string | null;
  delegation_intent: string | null;
  execution_mode: string | null;
  edge_type?: string;
}

interface Request {
  id: number;
  date_created: number;
  date_finished: number | null;
  request_id: string;
  task_id: string;
  agent_id: number;
  agent_name: string;
  agent_path: string | null;
  status: string;
  execution_time_ms: number | null;
  execution_mode: string;
  delegation_intent: string;
}

interface TaskAction {
  id: number | null;
  date_created: number | null;
  task_id: string;
  session_id: string | null;
  action_type: string;
  underlying_request_id: string | null;
  action_args: any;
  meta: any;
  status: string | null;
  response_time_ms: number | null;
  action_id: number | null;
  action_name: string;
  action_description: string | null;
  action_agent_id: number | null;
  action_agent_name: string;
}

interface TreeData {
  task_id: string;
  nodes: Node[];
  edges: Edge[];
  requests: Request[];
  actions?: TaskAction[];
}

interface AgentTaskTreeModalProps {
  taskId: string | null;
  isOpen: boolean;
  onClose: () => void;
}

const AgentTaskTreeModal: React.FC<AgentTaskTreeModalProps> = ({
  taskId,
  isOpen,
  onClose,
}) => {
  const { apiKey } = usePermissions();
  const [treeData, setTreeData] = useState<TreeData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hoveredAgentId, setHoveredAgentId] = useState<number | null>(null);
  const treeContainerRef = useRef<HTMLDivElement | null>(null);
  const nodeRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const [nodePositions, setNodePositions] = useState<
    Record<number, { x: number; y: number }>
  >({});

  useLayoutEffect(() => {
    if (!treeContainerRef.current || !treeData) {
      return;
    }

    const updatePositions = () => {
      const containerRect = treeContainerRef.current?.getBoundingClientRect();
      if (!containerRect) return;

      const positions: Record<number, { x: number; y: number }> = {};
      Object.entries(nodeRefs.current).forEach(([idStr, el]) => {
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const id = Number(idStr);
        positions[id] = {
          x: rect.left - containerRect.left + rect.width / 2,
          y: rect.top - containerRect.top + rect.height / 2,
        };
      });
      setNodePositions(positions);
    };

    updatePositions();

    const handleResize = () => updatePositions();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [treeData, isOpen]);

  useEffect(() => {
    if (!taskId || !isOpen) {
      return;
    }

    setLoading(true);
    const subscription = AgentService.getAgentTaskTree(
      taskId,
      undefined,
      apiKey
    ).subscribe({
      next: (response: any) => {
        setTreeData(response);
        setError(null);
        setLoading(false);
      },
      error: (err: any) => {
        console.error("Error loading task tree:", err);
        setError("Failed to load task tree");
        setLoading(false);
      },
    });

    return () => subscription.unsubscribe();
  }, [taskId, isOpen, apiKey]);

  const getConnectedAgents = (agentId: number): number[] => {
    if (!treeData) return [];
    const connected = new Set<number>();
    treeData.edges
      // Only include parent agents (from_agent_id) when this agent is the child (to_agent_id)
      // This matches the activeEdges logic which only draws edges TO the hovered agent
      .filter((edge) => edge.edge_type === "delegation")
      .forEach((edge) => {
        if (edge.to_agent_id === agentId) {
          connected.add(edge.from_agent_id);
        }
      });
    return Array.from(connected);
  };

  const buildTreeLevels = (): Node[][] => {
    if (!treeData) return [];

    const { nodes, edges } = treeData;
    const levels: Node[][] = [];
    const processed = new Set<number>();
    const nodeMap = new Map(nodes.map((n) => [n.agent_id, n]));

    const findRoots = (): Node[] => {
      const childIds = new Set(edges.map((e) => e.to_agent_id));
      return nodes.filter((n) => !childIds.has(n.agent_id));
    };

    const getChildren = (parentId: number): Node[] => {
      return edges
        .filter((e) => e.from_agent_id === parentId)
        .map((e) => nodeMap.get(e.to_agent_id))
        .filter((n): n is Node => n !== undefined);
    };

    let currentLevel = findRoots();
    if (currentLevel.length === 0 && nodes.length > 0) {
      currentLevel = [nodes[0]];
    }

    while (currentLevel.length > 0) {
      levels.push(currentLevel);
      currentLevel.forEach((n) => processed.add(n.agent_id));

      const nextLevel: Node[] = [];
      currentLevel.forEach((node) => {
        const children = getChildren(node.agent_id).filter(
          (c) => !processed.has(c.agent_id)
        );
        nextLevel.push(...children);
      });

      currentLevel = nextLevel;
    }

    return levels;
  };

  const renderTree = () => {
    if (!treeData) return null;

    const levels = buildTreeLevels();
    if (levels.length === 0) return null;

    const hasStructuralEdges =
      treeData.edges &&
      treeData.edges.some((edge) => edge.edge_type === "delegation");

    const activeEdges =
      hoveredAgentId && hasStructuralEdges
        ? treeData.edges.filter(
            (edge) =>
              edge.to_agent_id === hoveredAgentId &&
              edge.edge_type === "delegation"
          )
        : [];

    const NODE_RADIUS = 10;

    return (
      <div ref={treeContainerRef} className="relative overflow-x-auto">
        <svg
          className="absolute inset-0 pointer-events-none z-0"
          viewBox={`0 0 ${treeContainerRef.current?.clientWidth || 0} ${
            treeContainerRef.current?.clientHeight || 0
          }`}>
          {activeEdges.map((edge, idx) => {
            const from = nodePositions[edge.from_agent_id];
            const to = nodePositions[edge.to_agent_id];
            if (!from || !to) {
              return null;
            }

            // Anchor the connector at the bottom of the parent's label
            const deltaX = to.x - from.x;
            const direction = deltaX >= 0 ? 1 : -1;
            const startX = from.x;
            const startY = from.y + NODE_RADIUS + 18;

            const endX = to.x;
            const endY = to.y - NODE_RADIUS - 18; // stop with gap above the child circle
            const verticalDistance = endY - startY;
            const absDeltaX = Math.abs(deltaX);

            let path: string;
            if (absDeltaX < 40) {
              // Nearly vertical alignment: draw a simple straight connector
              path = `M ${startX} ${startY} L ${endX} ${endY}`;
            } else {
              // Horizontal separation: draw a smooth curved connector with inverted arc
              const midY = startY + verticalDistance * 0.5;
              const c1x = startX;
              const c2x = endX;
              const c1y = midY;
              const c2y = midY;
              path = `M ${startX} ${startY} C ${c1x} ${c1y}, ${c2x} ${c2y}, ${endX} ${endY}`;
            }

            return (
              <path
                key={`${edge.from_agent_id}-${edge.to_agent_id}-${idx}`}
                d={path}
                fill="none"
                stroke="#22c55e"
                strokeWidth={2}
              />
            );
          })}
        </svg>

        <div className="relative z-10 inline-flex flex-col gap-16 p-12 min-w-full">
          {levels.map((level, levelIndex) => (
            <div
              key={levelIndex}
              className="flex items-center justify-center gap-20">
              {level.map((node) => {
                const isHovered = hoveredAgentId === node.agent_id;
                const connectedAgents =
                  hoveredAgentId !== null
                    ? getConnectedAgents(hoveredAgentId)
                    : [];
                const isConnected =
                  hoveredAgentId !== null &&
                  connectedAgents.includes(node.agent_id);
                const isActiveNode =
                  hasStructuralEdges && (isHovered || isConnected);

                return (
                  <div
                    key={node.agent_id}
                    ref={(el) => {
                      nodeRefs.current[node.agent_id] = el;
                    }}
                    className="flex flex-col items-center gap-3"
                    onMouseEnter={() => {
                      if (!hasStructuralEdges) {
                        return;
                      }
                      setHoveredAgentId(node.agent_id);
                    }}
                    onMouseLeave={() => {
                      if (!hasStructuralEdges) {
                        return;
                      }
                      setHoveredAgentId(null);
                    }}>
                    {/* Circle node */}
                    <div
                      className={`
                        w-5 h-5 rounded-full transition-all cursor-pointer border
                        bg-maitai-green border-maitai-green
                        ${isActiveNode ? "scale-110 shadow-lg" : ""}
                      `}
                    />

                    {/* Agent name label */}
                    <div className="text-center">
                      <div className="text-xs font-gc-med text-foreground">
                        {node.agent_name}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>Task Tree Visualization</DialogTitle>
          {taskId && (
            <p className="text-sm text-muted-foreground font-mono">{taskId}</p>
          )}
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-6">
          {loading && (
            <Card className="border-border">
              <CardContent className="p-6">
                <Skeleton className="h-96 w-full" />
              </CardContent>
            </Card>
          )}

          {error && (
            <Card className="border-border">
              <CardContent className="p-6">
                <div className="text-center text-red-600">{error}</div>
              </CardContent>
            </Card>
          )}

          {!loading && !error && treeData && (
            <div className="space-y-4">
              <div className="bg-muted/20 rounded-lg border border-border">
                {renderTree()}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AgentTaskTreeModal;
